<template>
  <v-dialog v-model="dialog" persistent max-width="1000px">
    <template v-slot:activator="{ on }">
      <v-btn v-if="isEditButton" icon v-on="on">
        <v-icon>fa-edit</v-icon>
      </v-btn>
      <v-btn v-else color="info" dark v-on="on">
        <v-icon class="pr-1">fa-plus-circle</v-icon>
          Add New
      </v-btn>
    </template>

    <v-card>
      <v-card-title>
        <span class="text-h5">Holiday</span>
      </v-card-title>
      <v-card-text>
        <v-container>
          <v-row >

            <v-col cols="12">
              <text-box-date-picker
                label="Date"
                v-model="workingHoliday.holidayDate"
                v-on:input="onModification()"/>
            </v-col>

            <v-col cols="12">
              <v-text-field
                label="Holiday Name"
                ref="holidayName"
                v-model="workingHoliday.name"
                v-on:input="onModification()"
                :rules="[value => !!value || 'Required.']">
              </v-text-field>
            </v-col>

          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn id="cancelButton" large @click="cancel" :disabled="saving">Cancel</v-btn>
          <v-btn id="saveButton" large class="blue white--text" @click="save"
            :loading="saving" :disabled="!isModificationSavable">
              Save
          </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import Vue from 'vue';
import { mapState } from 'vuex';
import { TextBoxDatePicker } from '@kasasa/portal-components';
import {
  Component, Prop, Watch, Inject,
} from 'vue-property-decorator';
import { Holiday, HolidayResource }
  from '@/api/processingdays/';
import { Alert, NoticeClass } from '@kasasa/fbase-components/lib';

@Component({
  computed: mapState(['fiId']),
  components: {
    TextBoxDatePicker,
  },
})
export default class HolidayDialog extends Vue {
  @Inject('HolidayResource')
  holidayResource!:HolidayResource;

  @Prop()
  holiday!: Holiday;

  @Prop({ default() { return []; } })
  existingHolidays!:Array<Holiday>;

  @Prop()
  isEditButton!:boolean;

  workingHoliday:Holiday =
    HolidayDialog.createEmptyHoliday();

  fiId!:string;

  dialog:boolean = false;

  saving:boolean = false;

  isModificationSavable:boolean = false;

  loading = false;

  static createEmptyHoliday() : Holiday {
    return {
      id: undefined,
      name: '',
      holidayDate: new Date().toISOString().substring(0, 10),
    };
  }

  @Watch('dialog')
  onDialogToggle() {
    if (this.dialog) {
      this.saving = false;
      this.loading = true;
      this.initializeWorkingHoliday();
      this.loading = false;
    }
  }

  initializeWorkingHoliday() {
    this.isModificationSavable = false;
    if (this.holiday) {
      Object.assign(this.workingHoliday, this.holiday);
    } else {
      this.workingHoliday = HolidayDialog.createEmptyHoliday();
    }
  }

  cancel() {
    this.initializeWorkingHoliday();
    this.dialog = false;
  }

  save() {
    this.saving = true;
    if (!this.isNewHolidayDateUnique()) {
      this.saving = false;
      this.setError('Another holiday for the same date exists!');
      return;
    }
    this.holidayResource.save(this.fiId, this.workingHoliday)
      .then((savedHoliday:Holiday) => {
        if (this.holiday) {
          Object.assign(this.holiday, savedHoliday);
        }
        this.$emit('success', savedHoliday);
        this.dialog = false;
        this.initializeWorkingHoliday();
      })
      .catch((error:any) => {
        this.saving = false;
        this.showError();
      });
  }

  isNewHolidayDateUnique(): boolean {
    const allDates = new Set<string>();
    this.existingHolidays.forEach((holiday) => {
      if (holiday.id !== this.workingHoliday.id) {
        allDates.add(holiday.holidayDate);
      }
    });
    let isUnique = true;
    if (allDates.has(this.workingHoliday.holidayDate)) {
      isUnique = false;
    }
    return isUnique;
  }

  onModification() {
    this.isModificationSavable = this.workingHoliday.name !== '' // non-empty name
      && (
        !this.holiday // no exising holiday
           || (this.holiday // existing holiday
              && (this.holiday.holidayDate !== this.workingHoliday.holidayDate // date modified
                  || this.holiday.name !== this.workingHoliday.name)) // name modified
      );
  }

  showError() {
    const al = new Alert('An unexpected error occurred.', NoticeClass.ERROR);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);
  }

  setError(message:string) {
    const al = new Alert(message, NoticeClass.ERROR);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);
  }
}
</script>

<style lang="scss" scoped>
</style>
